/**
 * Green Bubble - productpagina: kolommen laten meelopen
 * ---------------------------------------------------------------------------
 * Op de productpagina staan twee blokken met twee kolommen naast elkaar:
 *
 *   1. productfoto  |  bestelblok (maat, potten, totaalprijs, USP's)
 *   2. omschrijving |  specificaties
 *
 * In allebei is de ene kolom fors korter dan de andere, waardoor er een leeg
 * gat overblijft. Deze laag zet de kórtste kolom vast zodra je langs de lange
 * kolom scrollt. Welke kolom dat is wordt gemeten, niet aangenomen - in blok 1
 * is dat meestal de foto, in blok 2 de specificaties, maar dat kan per plant
 * omdraaien.
 *
 * Past de korte kolom niet in beeld, dan wordt de offset negatief gemaakt.
 * Het effect: de kolom scrollt gewoon mee tot je zijn onderkant ziet en blijft
 * dan pas hangen. Zo blijft alle inhoud bereikbaar, ook op een laag scherm.
 *
 * Alles wordt herberekend bij resize, zoom, en bij elke inhoudswijziging van de
 * kolommen (de pottenkiezer groeit bijvoorbeeld als de kleurenrij verschijnt).
 *
 * Staat bewust los van assets/app.js - dat is een build-artefact uit de
 * src-repo van het bureau en wordt bij elke nieuwe build overschreven.
 */
(function () {
  'use strict';

  var BREAKPOINT = 992;    // onder deze breedte staan de kolommen onder elkaar
  var MARGE = 16;          // ademruimte boven en onder de vastgezette kolom
  var MIN_VERSCHIL = 100;  // onder dit hoogteverschil is vastzetten zinloos

  var blokken = [];
  var timer = null;
  var ro = null;

  function zichtbaar(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  // Hoogte van een eventuele vastgezette header, zodat de kolom daar niet onder schuift.
  function headerOffset() {
    var hoogste = 0;
    var kandidaten = document.querySelectorAll('header, .header, [class*="header"]');
    for (var i = 0; i < kandidaten.length; i++) {
      var el = kandidaten[i];
      if (!zichtbaar(el)) continue;
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') continue;
      var r = el.getBoundingClientRect();
      if (r.top > 4) continue;             // plakt niet aan de bovenkant
      if (r.height > 200) continue;        // geen overlay of drawer
      if (r.height > hoogste) hoogste = r.height;
    }
    return Math.round(hoogste);
  }

  // Twee kolommen naast elkaar binnen een Bootstrap-row. Bewust op vorm
  // gezocht en niet op klassenaam, zodat een herindeling dit niet breekt.
  function vindBlokken() {
    var rijen = document.querySelectorAll('main .container > .row, main .container-fluid > .row');
    var gevonden = [];

    for (var i = 0; i < rijen.length; i++) {
      var rij = rijen[i];
      var kols = [];
      for (var c = 0; c < rij.children.length; c++) {
        if (rij.children[c].nodeType === 1) kols.push(rij.children[c]);
      }
      if (kols.length !== 2) continue;
      if (!zichtbaar(kols[0]) || !zichtbaar(kols[1])) continue;
      gevonden.push({ rij: rij, kols: kols, actief: null });
    }
    return gevonden;
  }

  function naastElkaar(blok) {
    var a = blok.kols[0].getBoundingClientRect();
    var b = blok.kols[1].getBoundingClientRect();
    // Staan ze onder elkaar, dan overlappen ze niet horizontaal.
    return a.right <= b.left + 1 || b.right <= a.left + 1;
  }

  function losmaken(blok) {
    if (!blok.actief) return;
    blok.actief.classList.remove('gb-sticky-col');
    blok.actief.style.removeProperty('--gb-sticky-top');
    blok.actief = null;
  }

  // De kolommen zijn flex-items die standaard uitrekken tot de hoogte van de
  // langste. Meet je ze zo, dan zijn ze per definitie even hoog en valt er niets
  // te vergelijken. Daarom allebei even op hun eigen inhoud laten krimpen,
  // meten, en meteen weer terugzetten. Dat gebeurt binnen een frame, dus je
  // ziet er niets van.
  function meetInhoudshoogtes(blok) {
    var k0 = blok.kols[0];
    var k1 = blok.kols[1];
    var oud0 = k0.style.alignSelf;
    var oud1 = k1.style.alignSelf;

    k0.style.alignSelf = 'flex-start';
    k1.style.alignSelf = 'flex-start';

    var h0 = k0.getBoundingClientRect().height;
    var h1 = k1.getBoundingClientRect().height;

    k0.style.alignSelf = oud0;
    k1.style.alignSelf = oud1;

    return [h0, h1];
  }

  function verwerk(blok) {
    // Eerst loslaten, anders meet je de vastgezette hoogte terug.
    losmaken(blok);

    if (window.innerWidth < BREAKPOINT || !naastElkaar(blok)) return;

    // Extra zekerheid: overlappen de kolommen horizontaal ook maar een pixel,
    // dan staan ze in werkelijkheid onder elkaar en zou vastzetten betekenen dat
    // de ene kolom over de andere heen komt te liggen. Dat is precies het soort
    // kapotte weergave dat je niet wilt, dus dan liever niets doen.
    var ra = blok.kols[0].getBoundingClientRect();
    var rb = blok.kols[1].getBoundingClientRect();
    if (ra.right > rb.left + 1 && rb.right > ra.left + 1) return;

    var hoogtes = meetInhoudshoogtes(blok);
    var h0 = hoogtes[0];
    var h1 = hoogtes[1];
    if (Math.abs(h0 - h1) < MIN_VERSCHIL) return;

    var kort = h0 < h1 ? blok.kols[0] : blok.kols[1];
    var kortH = Math.min(h0, h1);

    var boven = headerOffset() + MARGE;
    var ruimte = window.innerHeight - kortH - MARGE;

    // Past de kolom niet in beeld? Dan een negatieve offset: hij scrollt mee
    // tot zijn onderkant zichtbaar is en blijft daarna hangen.
    var top = Math.min(boven, ruimte);

    kort.style.setProperty('--gb-sticky-top', Math.round(top) + 'px');
    kort.classList.add('gb-sticky-col');
    blok.actief = kort;
  }

  function observeer() {
    if (!ro) return;
    for (var i = 0; i < blokken.length; i++) {
      ro.observe(blokken[i].kols[0]);
      ro.observe(blokken[i].kols[1]);
    }
  }

  function verwerkAlles() {
    // De FAQ verhuist mee met de schermbreedte, dus dat moet vóór het meten:
    // de kolomhoogtes veranderen erdoor.
    zetFaqOpPlek();

    // Op mobiel hangt de FAQ als derde kolom in de row en slaat vindBlokken()
    // die row over. Bij elke herberekening opnieuw zoeken houdt beide situaties
    // kloppend, ook als je het venster over het breekpunt heen sleept.
    blokken = vindBlokken();
    observeer();

    for (var i = 0; i < blokken.length; i++) verwerk(blokken[i]);
  }

  function plan() {
    window.clearTimeout(timer);
    timer = window.setTimeout(verwerkAlles, 120);
  }

  // ---------------------------------------------------------------------------
  // FAQ-blok op zijn plek zetten
  // ---------------------------------------------------------------------------
  // Het blok wordt door Liquid gerenderd in layout/theme.liquid (dus het staat
  // gewoon in de HTML-bron, ook voor zoekmachines) en hier verplaatst. Reden: de
  // productsectie is een bestand van 90 KB dat via de API alleen in zijn geheel
  // te herschrijven is; dat risico is niet nodig voor een blok dat hier net zo
  // goed landt.
  //
  // Wáár het landt hangt af van de schermbreedte. Op desktop staan omschrijving
  // en specificaties naast elkaar en hoort de FAQ onder de specificaties: daar
  // vult hij de kortste kolom op. Op mobiel staan die kolommen onder elkaar en
  // kwam de FAQ dus tussen de specificaties en de omschrijving te staan - je
  // moest langs acht dichtgeklapte vragen scrollen voordat je las wat je koopt.
  // Op mobiel gaat hij daarom achter de omschrijving.
  var faqBlok = null;

  function plaatsFaq() {
    var bron = document.getElementById('gb-product-faq-source');
    if (bron) {
      faqBlok = bron.querySelector('.gb-product-faq');
      bron.parentNode && bron.parentNode.removeChild(bron);
    }
    zetFaqOpPlek();
  }

  function zetFaqOpPlek() {
    if (!faqBlok) return;

    var specs = document.querySelector('.specifications-accordion');
    var kolom = specs && specs.parentNode;
    var rij = kolom && kolom.parentNode;
    if (!kolom || !rij || !rij.classList || !rij.classList.contains('row')) return;

    if (window.innerWidth < BREAKPOINT) {
      // Als laatste flex-item in de row. order-3 is nodig omdat de twee kolommen
      // op mobiel al order-1 en order-2 dragen: zonder klasse zou de FAQ met
      // order 0 juist bovenaan belanden.
      if (faqBlok.parentNode !== rij) {
        faqBlok.classList.add('col-12', 'order-3', 'gb-faq-mobiel');
        rij.appendChild(faqBlok);
      }
      return;
    }

    if (faqBlok.previousElementSibling !== specs) {
      faqBlok.classList.remove('col-12', 'order-3', 'gb-faq-mobiel');
      kolom.insertBefore(faqBlok, specs.nextSibling);
    }
  }

  // Het voedingsblok hoort in het bestelformulier, vlak boven de totaalprijs:
  // daar zit de klant al te kiezen en telt de prijs meteen mee. Het wordt net als
  // de FAQ door Liquid gerenderd en hier verplaatst. De checkbox erin draagt de
  // haakjes van de bestaande add-ons, dus app.js pikt hem vanzelf op.
  function plaatsVoeding() {
    var bron = document.getElementById('gb-product-voeding-source');
    if (!bron) return;

    var blok = bron.querySelector('.gb-voeding');
    var totaal = document.querySelector('.product-price-summary');
    if (!blok || !totaal || !totaal.parentNode) {
      bron.parentNode && bron.parentNode.removeChild(bron);
      return;
    }

    // Het wenskaartje staat direct boven de totaalprijs. Het advies hoort bij de
    // plant en komt daarom vóór het kaartje: advies, kaartje, totaalprijs.
    var kaartje = totaal.previousElementSibling;
    var anker = (kaartje && kaartje.classList.contains('gb-kaartje')) ? kaartje : totaal;
    anker.parentNode.insertBefore(blok, anker);
    bron.parentNode.removeChild(bron);
  }

  // ---------------------------------------------------------------------------
  // Vaste balk onderin: de totaalprijs meenemen
  // ---------------------------------------------------------------------------
  // De balk toonde alleen de prijs van de plant zelf. Kies je een sierpot, laat
  // je oppotten of voeg je voeding toe, dan liep hij niet mee - terwijl juist
  // die balk het laatste is wat iemand ziet voor het klikken. Het blok
  // "Totaalprijs" wordt wel bijgewerkt door app.js, dus dat is de bron: wat daar
  // staat, komt hier ook te staan.
  //
  // Alleen de cijfers worden overgezet, niet de opmaak: de balk houdt zijn eigen
  // klassen, zodat er niets aan de styling verandert en app.js geen tweede kopie
  // van zijn eigen haakjes tegenkomt.
  // De twee blokken schrijven het hele bedrag net anders op. In het totaalblok
  // staat de komma in een eigen spannetje ("112" + <span>,</span>), in de balk zit
  // hij gewoon in de tekst ("112,"). Klakkeloos het getal overnemen liet daar dus
  // de komma verdwijnen. Vandaar: heeft het doel een eigen komma-element, dan
  // alleen het getal vervangen; zo niet, dan de hele tekst inclusief komma.
  function zetGetal(van, naar) {
    if (!van || !naar) return;
    var komma = naar.querySelector('.pdp-price-comma');
    if (!komma) {
      naar.textContent = van.textContent;
      return;
    }
    var getal = (van.firstChild && van.firstChild.nodeType === 3)
      ? van.firstChild.nodeValue
      : van.textContent.replace(/[^0-9]/g, '');
    var eerste = naar.firstChild;
    if (eerste && eerste.nodeType === 3) {
      eerste.nodeValue = getal;
    } else {
      naar.insertBefore(document.createTextNode(getal), komma);
    }
  }

  function kopieerBedrag(van, naar) {
    if (!van || !naar) return;
    var vk = van.children, nk = naar.children;
    if (vk.length >= 2 && nk.length >= 2) {
      zetGetal(vk[0], nk[0]);
      nk[1].textContent = vk[1].textContent;
    } else {
      naar.textContent = van.textContent;
    }
  }

  // De prijs achter "Oppotten" hangt af van de gekozen potmaat, dus app.js
  // herschrijft die tekst zelf. Dat gaat via window.GB_PRICE_FORMAT en zet het
  // valutateken dus meteen goed: geen teken voor kr en zl op de verkeerde plek,
  // en sinds kort ook het euroteken: €12,95 in het Nederlands, 12,95 € in
  // het Duits.
  //
  // Hier stond een opruimer die elk euroteken uit .js-addon-price-display sloopte,
  // omdat het thema de euro bewust zonder teken toonde. Die aanname klopt niet
  // meer: nu haalde hij juist het teken weg dat Liquid en app.js er expres in
  // zetten. Leeg gelaten in plaats van weggehaald, zodat de aanroep hieronder
  // blijft werken en in de diff te zien is wat er is veranderd.
  function haalEuroWeg() {}

  function syncBalk() {
    haalEuroWeg();

    var totaal = document.querySelector('.product-price-summary__total');
    var balk = document.querySelector('.product-cta .js-sticky-price');
    if (!totaal || !balk) return;

    kopieerBedrag(totaal.querySelector('.js-price-summary-total'),
                  balk.querySelector('.js-sticky-price-value'));

    var vanStreep = totaal.querySelector('.js-price-summary-compare');
    var naarStreep = balk.querySelector('.product-cta--sale');
    if (naarStreep) {
      var toon = !!(vanStreep && vanStreep.textContent.replace(/\s/g, '') &&
                    window.getComputedStyle(vanStreep).display !== 'none');
      if (toon) kopieerBedrag(vanStreep, naarStreep);
      naarStreep.style.display = toon ? '' : 'none';
    }
  }

  function volgTotaalprijs() {
    var totaal = document.querySelector('.product-price-summary__total');
    if (!totaal) return;

    syncBalk();

    if (window.MutationObserver) {
      // Het totaalblok wordt door app.js herschreven bij elke wijziging: pot,
      // oppotten, voeding, aantal, maat. Meekijken is betrouwbaarder dan al die
      // gebeurtenissen los proberen af te vangen.
      new MutationObserver(function () { syncBalk(); })
        .observe(totaal, { childList: true, subtree: true, characterData: true });
    }

    // Wisselt de maat, dan bouwt het thema de balk soms opnieuw op en zijn onze
    // cijfers weg. Na zo'n wijziging nog een paar keer nalopen.
    document.addEventListener('change', function () {
      window.setTimeout(syncBalk, 60);
      window.setTimeout(syncBalk, 400);
      window.setTimeout(syncBalk, 1200);
    }, true);
  }

  function start() {
    plaatsFaq();
    plaatsVoeding();
    volgTotaalprijs();

    // Inhoud die later binnenkomt: afbeeldingen, de pottenkiezer die zijn
    // kleurenrij uitklapt, lazy geladen blokken. De waarnemer wordt hier
    // aangemaakt en in verwerkAlles() op de gevonden kolommen gezet, omdat die
    // set wisselt zodra de FAQ over het breekpunt heen verhuist.
    if (window.ResizeObserver) ro = new ResizeObserver(plan);

    verwerkAlles();

    window.addEventListener('resize', plan);
    window.addEventListener('orientationchange', plan);

    // Zodra je onder de breekpuntbreedte komt moet alles meteen los, niet pas na
    // de vertraging van plan(). Anders zie je bij het kantelen van een telefoon
    // heel even een vastgezette kolom over de andere heen liggen.
    if (window.matchMedia) {
      var mq = window.matchMedia('(min-width: ' + BREAKPOINT + 'px)');
      var opBreedte = function () {
        if (!mq.matches) {
          for (var i = 0; i < blokken.length; i++) losmaken(blokken[i]);
        }
        plan();
      };
      if (mq.addEventListener) mq.addEventListener('change', opBreedte);
      else if (mq.addListener) mq.addListener(opBreedte);
    }

    window.addEventListener('load', plan);
    window.setTimeout(verwerkAlles, 1200);
    window.setTimeout(verwerkAlles, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
